import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { FavoritesApi } from '../../api/favorites.api';
import { DashboardState } from '../dashboard.state';
import { FavoritesAction } from './favorites.action';
import { selectFavoritesState } from './favorites.selectors';

const CACHE_VALIDITY = 180000;

@Injectable()
export class FavoritesEffects {
  getFavorites$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FavoritesAction.getFavorites),
        concatLatestFrom(() => this.store.select(selectFavoritesState)),
        mergeMap(([loadAction, favoritesState]) => {
          const { count, start } = loadAction;
          const end = start + count - 1;
          if (start <= 0) {
            // Load from start
            const cacheValid = this.isCacheValid(favoritesState.lastUpdate);
            return of({
              count,
              reset: cacheValid,
              type: '[Favorites] Load From Start',
            });
          }
          const { page, pageSize } = this.computePagesAndSizeFromInterval(
            start,
            end
          );
          return this.favoritesApi.getMyFavoritesVideos(page, pageSize).pipe(
            map((payload) => ({
              page,
              pageSize,
              payload,
              type: '[Favorites] Favorites Loaded',
            })),
            catchError(() => {
              console.error('Unable to load favorites.');
              return EMPTY;
            })
          );
        })
      );
    },
    {
      dispatch: true,
    }
  );

  loadFavoritesFromStart$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FavoritesAction.loadFromStart),
        concatLatestFrom(() => this.store.select(selectFavoritesState)),
        mergeMap(([{ count }, favoritesState]) => {
          const end = count - 1;
          const cacheValid = this.isCacheValid(favoritesState.lastUpdate);
          let startIndexToLoad = 0;
          if (cacheValid) {
            startIndexToLoad = favoritesState.lastRequestedIndex + 1;
          }
          if (startIndexToLoad > end) {
            return of({ type: '[Favorites] Already loaded' });
          }
          const { page, pageSize } = this.computePagesAndSizeFromInterval(
            0,
            end
          );
          return this.favoritesApi.getMyFavoritesVideos(0, pageSize).pipe(
            map((payload) => ({
              page,
              pageSize,
              payload,
              type: '[Favorites] Favorites Loaded',
            })),
            catchError(() => {
              console.error('Unable to load favorites.');
              // TODO : Stop loading !!
              return EMPTY;
            })
          );
        })
      );
    },
    {
      dispatch: true,
    }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly favoritesApi: FavoritesApi,
    // eslint-disable-next-line ngrx/no-typed-global-store
    private readonly store: Store
  ) {}

  private isCacheValid(lastUpdateTime: number): boolean {
    return lastUpdateTime + CACHE_VALIDITY > Date.now();
  }

  protected computePagesAndSizeFromInterval(
    start: number,
    end: number
  ): { page: number; pageSize: number } {
    const numberWantedItems = end - start + 1;
    if (start <= 0 || start < numberWantedItems) {
      return {
        page: 1,
        pageSize: end + 1,
      };
    }

    for (let size = numberWantedItems; size <= start; ++size) {
      const pageBefore = Math.floor(start / size);
      const newStart = pageBefore * size;
      if (newStart + size > end) {
        return {
          page: pageBefore + 1,
          pageSize: size,
        };
      }
    }

    // Should never go here
    return {
      page: 1,
      pageSize: end + 1,
    };
  }
}

function load(start: number, count: number) {}
